import { isDefined } from "@clipboard-health/util-ts";
import { Chip, type SxProps, type Theme } from "@mui/material";
import { useSession } from "@src/appV2/Auth/api/useSession";
import {
  EXPIRED_SHIFT_TIME_PROPOSAL_CHIP,
  formatShiftStartEndDateTime,
  SHIFT_TIME_PROPOSAL_STATUS_CHIP_MAP,
} from "@src/appV2/Chat/utils";
import { SHIFT_TIME_PROPOSAL_STATUS } from "@src/appV2/ShiftTimeProposals/api/schema";
import { useGetShiftTimeProposalById } from "@src/appV2/ShiftTimeProposals/api/useShiftTimeProposals";
import { PACIFIC_TIME_ZONE } from "@src/appV2/Timezone/constants";
import { isPast } from "date-fns";

import { ChatMessageCard } from "./ChatMessageCard";
import { useTu } from "@src/appV2/i18n/useTu";

interface ShiftTimeProposalCreatedMessageProps {
  shiftTimeProposalId: string;
  originalStartTime: string;
  originalEndTime: string;
  sx?: SxProps<Theme>;
}

/**
 * Renders a shift time proposal message with rich card UI.
 * Shows the proposed shift time, original shift time, and status chip.
 * Detects expiration based on whether the proposed start time has passed.
 */
export function ShiftTimeProposalCreatedMessage(props: ShiftTimeProposalCreatedMessageProps) {
  const { tu } = useTu("chat");
  const { shiftTimeProposalId, originalStartTime, originalEndTime, sx } = props;

  const { profile } = useSession();
  const { data: shiftTimeProposalResponse } = useGetShiftTimeProposalById(
    { shiftTimeProposalId },
    { enabled: isDefined(shiftTimeProposalId) }
  );

  const shiftTimeProposal = shiftTimeProposalResponse?.data;

  if (!isDefined(shiftTimeProposal)) {
    // Don't render anything until data loads
    return null;
  }

  const { status, proposedStartTime, proposedEndTime } = shiftTimeProposal.attributes;
  const timeZone = profile?.tmz ?? PACIFIC_TIME_ZONE;

  const proposedShiftStartEnd = formatShiftStartEndDateTime(
    proposedStartTime,
    proposedEndTime,
    timeZone
  );

  const originalShiftStartEnd = formatShiftStartEndDateTime(
    originalStartTime,
    originalEndTime,
    timeZone
  );

  // A proposal is expired if it's still in PROPOSED status but the proposed start time has passed
  const isExpired =
    status === SHIFT_TIME_PROPOSAL_STATUS.PROPOSED && isPast(new Date(proposedStartTime));

  const chipProps = isExpired
    ? EXPIRED_SHIFT_TIME_PROPOSAL_CHIP
    : SHIFT_TIME_PROPOSAL_STATUS_CHIP_MAP[status];

  const statusChip = (
    <Chip
      aria-label={chipProps.label}
      color={chipProps.color}
      label={chipProps.label}
      size="small"
      sx={{
        fontWeight: "bold",
        ...chipProps.customStyles,
      }}
      variant="filled"
    />
  );

  return (
    <ChatMessageCard
      caption={`Previous: ${originalShiftStartEnd}`}
      statusChip={statusChip}
      subtitle={proposedShiftStartEnd}
      sx={sx}
      title={tu("You proposed a new shift time")}
    />
  );
}
